import { useSyncExternalStore } from 'react';

/**
 * Search-results display switch (Trev, Oct 17 2026: "show everything" while
 * the live key serves sandbox prices). Same Firestore doc the server reads:
 * site_config/search_display.hideNoEvRows (public read). Missing = false.
 *
 * While it is off:
 *   · rows with no EV are shown (price + a "No EV" label), sorted last;
 *   · the automatic min-books defaults (5+ sportsbooks, the 2-book prop floor,
 *     3+ My Books) are "All" (My Books: any one of your books). A minimum the
 *     user set to something other than the default still applies, as do the
 *     filters they pick (+EV, My Books, a sportsbook, a card's Min control).
 *
 * The value lives in a module-level store so pure helpers
 * (computeFilteredAndSortedOptions, resultCounts) can read it;
 * SiteConfigProvider writes it from the Firestore snapshot.
 */

export const DEFAULT_MIN_BOOKS_STRAIGHTS = 5;
export const DEFAULT_MIN_BOOKS_MY_BOOKS = 3;
export const DEFAULT_MIN_BOOKS_SGP = 5;


let _hideNoEvRows = false;
const _listeners = new Set();

export function setHideNoEvRows(v) {
    const next = v === true;
    if (next === _hideNoEvRows) return;
    _hideNoEvRows = next;
    for (const fn of _listeners) {
        try { fn(); } catch { /* no-op */ }
    }
}

function _subscribe(fn) {
    _listeners.add(fn);
    return () => _listeners.delete(fn);
}

/** React hook: re-renders when the switch changes. */
export function useHideNoEvRows() {
    return useSyncExternalStore(_subscribe, getHideNoEvRows, getHideNoEvRows);
}

export function getHideNoEvRows() {
    return _hideNoEvRows;
}

function _resolve(hideNoEvRows) {
    return typeof hideNoEvRows === 'boolean' ? hideNoEvRows : _hideNoEvRows;
}

/**
 * The results-level minimum book count for straights.
 * Hiding on: exactly the old rule, `parseInt(pref) || 5`.
 * Hiding off: 0 ("All") unless the user saved a minimum other than the default.
 */
export function effectiveMinBooksStraights(prefValue, hideNoEvRows) {
    const n = parseInt(prefValue, 10);
    if (_resolve(hideNoEvRows)) return n || DEFAULT_MIN_BOOKS_STRAIGHTS;
    if (!Number.isFinite(n) || n === DEFAULT_MIN_BOOKS_STRAIGHTS) return 0;
    return Math.max(0, n);
}

/**
 * The minimum book count for SGPs. Hiding on: the old rule, `parseInt(pref) || 5`.
 * Hiding off: 0 ("All") unless the user saved a minimum other than the default.
 * Nov 3, 2026: SGPs average 1–2 pricing books outside hockey, so the 5+
 * default hid most of them in proving mode, unlike straights.
 */
export function effectiveMinBooksSGP(prefValue, hideNoEvRows) {
    const n = parseInt(prefValue, 10);
    if (_resolve(hideNoEvRows)) return n || DEFAULT_MIN_BOOKS_SGP;
    if (!Number.isFinite(n) || n === DEFAULT_MIN_BOOKS_SGP) return 0;
    return Math.max(0, n);
}

/**
 * How many of the user's books must price a row in My Books mode.
 * Hiding on: `parseInt(pref) || 3`. Hiding off: 1 (any of your books) unless
 * the user saved a value other than the default.
 */
export function effectiveMinMyBooks(prefValue, hideNoEvRows) {
    const n = parseInt(prefValue, 10);
    if (_resolve(hideNoEvRows)) return n || DEFAULT_MIN_BOOKS_MY_BOOKS;
    if (!Number.isFinite(n) || n === DEFAULT_MIN_BOOKS_MY_BOOKS) return 1;
    return Math.max(1, n);
}

/** Server-marked no-EV row (functions/utils/noEvRows.js). */
export function isNoEvRow(rawOrOption) {
    const r = rawOrOption?.raw || rawOrOption || {};
    return r._noEv === true || rawOrOption?._noEv === true;
}

/**
 * The results page's min-books banner (SgpFlowScreenView). Hiding on: exactly
 * the pre-Oct-17 expressions. Hiding off: the effective minimums above.
 *
 * @returns {{ showStraightsBanner: boolean, minVal: number, relaxedMin: number }}
 */
export function minBooksBannerState(prefs, { hideNoEvRows, myBooksMode = false, myBooksCount = 0 } = {}) {
    const p = prefs || {};
    const hide = _resolve(hideNoEvRows);
    if (hide) {
        return {
            showStraightsBanner: (p.minBooksStraights || 1) > 1,
            minVal: myBooksMode ? (p.minBooksMyBooks ?? 3) : (p.minBooksStraights || 5),
            relaxedMin: parseInt(p.minBooksStraights, 10) || 5,
        };
    }
    const straights = effectiveMinBooksStraights(p.minBooksStraights, false);
    const my = effectiveMinMyBooks(p.minBooksMyBooks, false);
    return {
        showStraightsBanner: straights > 1,
        minVal: myBooksMode ? Math.min(my, Math.max(1, myBooksCount)) : straights,
        relaxedMin: Math.max(1, straights),
    };
}
